import { useEffect, useState } from "react";

/**
 * BulletShapePicker — listens for `crescendo:bullet-shape-pick` events
 * fired by the document editor when a user clicks (or double-clicks) on
 * a bullet glyph. Renders a Google-Docs-style icon menu of bullet
 * styles. Single-click → applies to that one <li>; double-click →
 * applies to every <li> in the parent list (UL/OL).
 *
 * Iter35 user request: "There should be a huge variety of different
 * point types (not including numbers, letters, or checklists as icons
 * like we have now), it should look like the menu on Google Docs. The
 * same should happen when single-clicking a numbered list, being able
 * to change it to lettered, or roman numerical should be the options
 * under the big menu to change the icon."
 */

// Visual icon shapes — each becomes a `list-style-image` so any glyph
// works regardless of CSS list-style-type support. We use inline SVG
// data URIs so the icon shows up without a network round-trip.
const svgUri = (path, color = "%23A1A1AA") =>
  `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='${color}' stroke='${color}' stroke-width='1.5'%3E${path}%3C/svg%3E")`;

const ICON_SHAPES = [
  // Native list-style-types (no SVG needed) — kept first because they
  // render perfectly with the browser's spacing.
  { id: "disc",   label: "•", listStyleType: "disc",   list: "ul" },
  { id: "circle", label: "○", listStyleType: "circle", list: "ul" },
  { id: "square", label: "▪", listStyleType: "square", list: "ul" },
  // SVG icon shapes — `listStyleImage` is the native CSS hook.
  { id: "arrow_right", label: "→", listStyleImage: svgUri("%3Cpath d='M5 12h14M13 5l7 7-7 7' fill='none'/%3E"), list: "ul" },
  { id: "chevron",     label: "›", listStyleImage: svgUri("%3Cpath d='M9 6l6 6-6 6' fill='none'/%3E"), list: "ul" },
  { id: "triangle",    label: "▸", listStyleImage: svgUri("%3Cpath d='M8 5v14l11-7z'/%3E"), list: "ul" },
  { id: "star",        label: "★", listStyleImage: svgUri("%3Cpath d='M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61z'/%3E"), list: "ul" },
  { id: "diamond",     label: "◆", listStyleImage: svgUri("%3Cpath d='M12 2l10 10-10 10L2 12z'/%3E"), list: "ul" },
  { id: "heart",       label: "♥", listStyleImage: svgUri("%3Cpath d='M12 21s-7-4.5-9-9.5C1.5 7 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 22.5 7 21 11.5 19 16.5 12 21 12 21z'/%3E"), list: "ul" },
  { id: "hash",        label: "#", listStyleImage: svgUri("%3Cpath d='M4 9h16M4 15h16M10 3L8 21M16 3l-2 18' fill='none'/%3E"), list: "ul" },
  { id: "plus",        label: "+", listStyleImage: svgUri("%3Cpath d='M12 5v14M5 12h14' fill='none'/%3E"), list: "ul" },
  { id: "minus",       label: "−", listStyleImage: svgUri("%3Cpath d='M5 12h14' fill='none'/%3E"), list: "ul" },
  { id: "dot_filled",  label: "●", listStyleImage: svgUri("%3Ccircle cx='12' cy='12' r='5'/%3E"), list: "ul" },
  { id: "dot_outline", label: "◯", listStyleImage: svgUri("%3Ccircle cx='12' cy='12' r='6' fill='none'/%3E"), list: "ul" },
  { id: "asterisk",    label: "✱", listStyleImage: svgUri("%3Cpath d='M12 2v20M3.5 7l17 10M3.5 17l17-10' fill='none'/%3E"), list: "ul" },
  { id: "leaf",        label: "❀", listStyleImage: svgUri("%3Cpath d='M12 4c4 0 8 4 8 8 0 0-4-4-8-4s-8 4-8 4c0-4 4-8 8-8z'/%3E"), list: "ul" },
];

// Numbered/lettered/roman variants — second tab when target list is OL.
const NUMBERED_SHAPES = [
  { id: "decimal",      label: "1.", listStyleType: "decimal",      list: "ol" },
  { id: "decimal-zero", label: "01.", listStyleType: "decimal-leading-zero", list: "ol" },
  { id: "lower-alpha",  label: "a.", listStyleType: "lower-alpha",  list: "ol" },
  { id: "upper-alpha",  label: "A.", listStyleType: "upper-alpha",  list: "ol" },
  { id: "lower-roman",  label: "i.", listStyleType: "lower-roman",  list: "ol" },
  { id: "upper-roman",  label: "I.", listStyleType: "upper-roman",  list: "ol" },
];

export function BulletShapePicker() {
  const [pick, setPick] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onPick = (e) => { setPick(e.detail); setQuery(""); };
    const onClickAway = (e) => {
      if (!pick) return;
      if (e.target.closest && e.target.closest("[data-bullet-picker]")) return;
      setPick(null);
    };
    window.addEventListener("crescendo:bullet-shape-pick", onPick);
    window.addEventListener("mousedown", onClickAway);
    return () => {
      window.removeEventListener("crescendo:bullet-shape-pick", onPick);
      window.removeEventListener("mousedown", onClickAway);
    };
  }, [pick]);

  if (!pick) return null;

  const apply = (shape) => {
    const list = pick.list;
    if (!list) { setPick(null); return; }
    const targetIsAll = pick.target === "all";
    // If shape's list type doesn't match the current parent (e.g. ordered
    // shape on a UL or vice versa), first replace the parent. Migrate
    // children + preserve text content + merge with adjacent inline.
    if (list.tagName?.toLowerCase() !== shape.list) {
      const replacement = document.createElement(shape.list);
      while (list.firstChild) replacement.appendChild(list.firstChild);
      list.replaceWith(replacement);
      if (shape.listStyleType) replacement.style.listStyleType = shape.listStyleType;
      if (shape.listStyleImage) replacement.style.listStyleImage = shape.listStyleImage;
    } else if (targetIsAll) {
      list.style.listStyleType = shape.listStyleType || "";
      list.style.listStyleImage = shape.listStyleImage || "";
      list.querySelectorAll(":scope > li").forEach((li) => {
        li.style.listStyleType = "";
        li.style.listStyleImage = "";
      });
    } else if (pick.target instanceof HTMLElement) {
      pick.target.style.listStyleType = shape.listStyleType || "";
      pick.target.style.listStyleImage = shape.listStyleImage || "";
    }
    window.dispatchEvent(new CustomEvent("crescendo:bullet-shape-applied"));
    setPick(null);
  };

  // Show only the icon palette by default, with a link to the
  // numbered/lettered/roman options below — Google-Docs UX.
  // Constrain to viewport so the menu doesn't overflow off-screen
  // (iter35 user complaint: "right-click menu overflows the screen").
  const VW = window.innerWidth, VH = window.innerHeight;
  const PICKER_W = 330, PICKER_H = 360;
  const safeX = Math.min(pick.x, VW - PICKER_W - 12);
  const safeY = Math.min(pick.y + 14, VH - PICKER_H - 12);

  return (
    <div
      className="fixed z-[300] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-3 w-[330px]"
      style={{ left: Math.max(8, safeX), top: Math.max(8, safeY) }}
      data-bullet-picker
      data-testid="bullet-shape-picker"
    >
      {/* Iter39 user request: searchable mega-menu. */}
      <div className="mb-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search bullets…"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1.5 text-[11px] text-[#EDEDED] outline-none focus:border-[#5A90F5]"
          data-testid="bullet-shape-search"
        />
      </div>
      {/* Bullet icons — main palette */}
      <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1.5 px-0.5">Bullet icons</div>
      <div className="grid grid-cols-8 gap-1 mb-3">
        {ICON_SHAPES.filter((s) => !query || s.id.toLowerCase().includes(query.toLowerCase()) || (s.label || "").includes(query)).map((s) => (
          <button
            key={s.id}
            onClick={() => apply(s)}
            className="w-9 h-9 flex items-center justify-center rounded hover:bg-[#1a1a1a] text-base text-[#EDEDED] border border-transparent hover:border-[#5A90F5] transition-colors"
            title={s.id}
            data-testid={`bullet-shape-${s.id}`}
          >
            <span className="leading-none">{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
